/**
 * What a cue from beat 1 *means*, in a scene made of triangles.
 *
 * `src/story/act1/born.ts` says "the lamp is blocked". This file is the only
 * thing that knows a lamp is a `THREE.PointLight`, that a door is a leaf on a
 * hinge, and that boots walk at 0.62 m/s. Keeping those two apart is what lets
 * the beat be replayed in Node with no renderer at all, and it is the same seam
 * `gameAudio.ts` keeps between "she is trotting" and "play this buffer".
 *
 * **Everything here runs on the RENDER step.** It is presentation: it reads the
 * beat's cues and moves meshes, and it never writes to the simulation. The one
 * exception is deliberate and marked — the camera hold, which the game asks
 * about rather than being told.
 */

import * as THREE from 'three';
import { BORN_CUES } from '@/story/act1/born';
import { RosalieRig } from '@/anim/rig';
import { AnimationPlayer, ROSALIE_ANIM } from '@/anim/player';
import { ROSALIE_CLIPS } from '@/anim/clips';
import { neutralPose, type Pose } from '@/anim/types';
import type { CharacterState } from '@/physics/character';
import type { SiblingSnapshot } from '@/world/bornSibling';
import { findWorkerParts, type WorkerParts } from '@/models/env/worker';
import { DoorSwing } from '@/world/doorSwing';
import { WALKWAY_DOOR_AT } from '@/world/farrowing';
import { FadeOverlay } from '@/world/fadeOverlay';
import type { Cue } from '@/story/types';
import type { LivePenGate } from '@/world/livePenGate';
import { PEN_GATE } from '@/models/env/pen-gate';
import {
  AWAKE_SIBLING_CENTRE_Y,
  BAY_Z,
  BORN_GATE_TAKE_OPEN,
  BORN_LATCH_STANCE,
  BORN_MOUTH_STANCE,
  BORN_STEP_IN,
  BORN_WALK_IN,
  DECK_Y,
  LAMP_AT,
  SIBLING_TAKEN_AT,
  SOW_HEAD_LIFT_MAX,
  type BornBay,
  type LittermateParts,
} from '@/world/bornBay';
import { gentleCutawayExtraSeconds } from '@/shell/gentle';
import type { Framing, ShotFrame } from '@/camera/framing';

/** How fast a man walks down a hall he has walked down ten thousand times. */
const BOOT_SPEED = 0.62;

/** One stride, metres. Drives the footfall sound and nothing else. */
const BOOT_STRIDE = 0.62;

/** Both siblings breathe more quickly than their mother, but never mechanically. */
const LITTER_BREATHS_PER_MINUTE = 34;
const LITTER_BREATH_AMPLITUDE = 0.025;
const LITTER_TWITCH_DURATION = 1.1;
const LITTER_TWITCH_AMPLITUDE = 0.12;

/**
 * When the carried sibling cries again, in seconds after the boots turn to
 * leave, and how loud (D-250).
 *
 * Four, and the gaps widen: a piglet screams itself out, and it is being
 * carried away. The first two are at the gate, while he steps out and swings
 * it shut. The last two fall after he is through the walkway door, where
 * `DOOR_MUFFLE` takes most of them — the door is what cuts it off, and
 * then there is nothing. Against his part: stepping out, the swing and the
 * latch take 2.6 s, and from the latch to the door's plane is 0.7 m at
 * 0.62 m/s, so he is through it at about 3.7 s, later by up to
 * `MOUTH_GUARD_SECONDS` if she is at the mouth. `beat-part-module.spec.ts`
 * asserts the first and last, with her there.
 */
export const CARRIED_CRY_AT: readonly (readonly [number, number])[] = [
  [0.35, 0.95],
  [1.65, 0.85],
  [4.2, 0.8],
  [5.8, 0.7],
];
/**
 * The awake plush's roll pivot: its body centre above the straw standing, and
 * its half-width, which is how high that centre sits once it is on its side.
 * `ROSALIE_SCALE` (0.32 tall, 0.30 wide) with the legs' share taken off the
 * height; `bornBay.ts` hangs the plush this far below the pivot.
 */
const AWAKE_SIBLING_HALF_WIDTH = 0.15;

/** What is left of a cry from the far side of the walkway door. */
export const DOOR_MUFFLE = 0.3;

/**
 * Where he is in the taking (beat 1 revision 2, C): he walks in, lifts the
 * latch, swings the gate open, steps into the mouth, bends in for her
 * littermate, steps out, swings it shut, drops the latch, and goes.
 *
 * Until then he stopped on the walkway and a dark slab came down over the
 * creep, and Alex's review said what that was: the worker never opens
 * anything. The dark is now his body bent into the pen.
 */
type TakingPhase =
  | 'parked' | 'walkIn' | 'unlatch' | 'swingOpen' | 'stepIn' | 'atMouth'
  | 'stepOut' | 'swingShut' | 'latchDrop' | 'walkOut' | 'gone';

/** Reaching for the latch, and the moment in it the latch lifts. */
const UNLATCH_SECONDS = 0.6;
const LATCH_LIFT_AT = 0.3;
/** The gate's swing either way, eased. */
const GATE_SWING_SECONDS = 1.0;
/** The latch seats, and he turns to go. */
const LATCH_DROP_SECONDS = 0.4;
/** How far he bends at the waist for the latch, and into the pen for the take. */
const BEND_LATCH = 0.3;
const BEND_TAKE = 1.15;

/**
 * He waits this long, at most, for her to come away from the mouth before he
 * swings the gate open or steps out of it.
 *
 * Beat 1 never moves the gate's collider: she cannot leave the pen, and
 * nothing she can see says otherwise while he stands in the mouth (the open
 * leaf and his body close it, `born-gate.spec.ts`). Either side of that, for
 * the second the leaf takes to swing, the mouth stands open and empty, and if
 * she is pressed against it she meets the shut collider in plain view. So he
 * waits for her. A wait with no end would be a softlock (spec §10), and the
 * story's stages are on a clock and would not wait with him, so it ends.
 */
const MOUTH_GUARD_SECONDS = 1.5;
/** Her centre this near the mouth's line holds him. */
const MOUTH_GUARD_RADIUS = 0.4;

type Point = { readonly x: number; readonly z: number };

/** Total length of a polyline, metres. */
function pathLength(path: readonly Point[]): number {
  let length = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    if (a !== undefined && b !== undefined) length += Math.hypot(b.x - a.x, b.z - a.z);
  }
  return length;
}

/** The point `s` metres along a polyline, and the heading of its segment (0 faces +Z). */
function pointOnPath(path: readonly Point[], s: number): { x: number; z: number; heading: number } {
  let left = s;
  let last = { x: path[0]?.x ?? 0, z: path[0]?.z ?? 0, heading: 0 };
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    if (a === undefined || b === undefined) continue;
    const length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-9) continue;
    const heading = Math.atan2(b.x - a.x, b.z - a.z);
    if (left <= length) {
      const k = left / length;
      return { x: a.x + (b.x - a.x) * k, z: a.z + (b.z - a.z) * k, heading };
    }
    left -= length;
    last = { x: b.x, z: b.z, heading };
  }
  return last;
}

/** Wrapped to (−π, π], so a turn goes the short way round. */
function wrapAngle(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

/** Her centre's distance to the mouth's line on the kerb, in plan. */
function distanceToMouth(her: Point, gate: LivePenGate): number {
  const zFrom = gate.hinge.z - gate.swing * PEN_GATE.length;
  const z = Math.min(Math.max(her.z, Math.min(zFrom, gate.hinge.z)), Math.max(zFrom, gate.hinge.z));
  return Math.hypot(her.x - gate.hinge.x, her.z - z);
}

/** Seconds after the squeal in the dark that her mother answers it. */
const SOW_ALARM_GRUNTS: readonly number[] = [0.3, 1.15, 2.3];

/**
 * Where the carried sibling's body centre sits in the worker's right hand, in
 * that shoulder's local frame: level with the glove (0.65 m down the arm),
 * just outboard of his leg, facing where he faces. Held around the chest,
 * legs hanging — and at 0.72 m, above the 0.55 m kerb she watches it over.
 */
const CARRY_IN_HAND = { x: 0.1, y: -0.72, z: 0.06 } as const;

/** `window.__rosalie.beatDebug`: beat 1's view, read off the live scene. */
export interface BornDebug {
  lampLevel: number;
  /** The director's measured arrival, not a re-derived mesh position. */
  lean: number;
  /** The live batten ratio, so a requested dim cannot masquerade as a real one. */
  nearLightLevel: number;
  fade: number;
  cameraHeld: boolean;
  /** Whether the littermate is still in the scene. The beat's one invariant. */
  siblingPresent: boolean;
  /** D-250: the awake sibling's sim phase, as the director last presented it. */
  siblingPhase: SiblingSnapshot['phase'];
  /** Whether the littermate is in the worker's hand. */
  carried: boolean;
  /** Cries played since the taking, the one in the dark included. */
  cries: number;
  /**
   * Where the littermate is in the world while it is in his hand, else null.
   * Read off the scene graph, so a frame aimed at it is aimed at what renders.
   */
  carriedAt: { x: number; y: number; z: number } | null;
}

/**
 * The opening shot: her face, then the crate.
 *
 * It starts a little over half a metre from her on the lamp side of the creep,
 * three-quarters on to her face, and ends low and outside the bars, looking back
 * in past them. The end framing is the important one and it is the reason the
 * shot exists at all: **from underneath and outside, the bars and her mother's
 * size are the same fact**, and no other angle in the room says that.
 *
 * It ends *low*, at 0.30 m, rather than pulling up to a conventional
 * establishing height. The whole game is seen from 0.32 m and the first shot
 * should not lie about that — and the pen enforces it anyway: the kerb is 0.55 m
 * of concrete and there is nowhere above it for a lens to be that is not inside
 * it.
 */
const REVEAL_FROM: ShotFrame = {
  // In FRONT of her along the creep, looking back at her face — which is what
  // the previous version of this comment also claimed, and it was not true.
  //
  // ── R-024, and what the sweep actually found ────────────────────────────────
  //
  // This used to be `x: +0.46` with the aim at her body centre, and it produced
  // the frame Alex said does not read. Three separate faults, and only the sweep
  // separated them (`npm run shots:opening`, 20 frames with measurements):
  //
  //  1. **She was pointing the wrong way**, so +X was her flank and not her
  //     face. Fixed at the source — `BORN_SPAWN` now carries a facing.
  //  2. **0.46 m is not a portrait distance for a 0.55 m animal.** Her body
  //     subtended 1.9 times the frame height; every facing was a wall of her.
  //  3. **The +X side has no room to stand back in.** The kerb's inner face is
  //     0.73 m from her, so 0.70 m of stand-off puts the lens *in the concrete*
  //     — measured, four frames of the inside of a kerb. The creep's 2.1 m of
  //     length is all on the other side, which is also the side the heat lamp
  //     is on.
  //
  // So the lens moved to the lamp side and stood back. The aim is her HEAD, and
  // the offset below is measured rather than guessed: at the spawn facing her
  // head's centre sits at (−0.09, 0.31, +0.13) from her origin. That number is
  // only a constant because the facing is one — change `BORN_SPAWN.facing` and
  // this has to be re-measured, which is what the sweep is for.
  position: { x: -0.52, y: 0.20, z: 0.20 },
  lookAt: { x: -0.09, y: 0.31, z: 0.13 },
  fov: 44,
};

const REVEAL_TO: ShotFrame = {
  // Back down the creep and turned to look ACROSS the lane: the partition rails
  // in the near ground, her mother filling everything behind them.
  //
  // This is the frame the whole shot exists to arrive at, and it is the only
  // angle in the room where the bars and her mother's size are one fact rather
  // than two. Going up and out over the pen kerb would say it more grandly and
  // is not available: the kerb is 0.55 m of solid concrete and the lens would
  // pass straight through it, which spec §4 forbids in as many words.
  position: { x: -0.72, y: 0.3, z: -0.16 },
  lookAt: { x: 0.3, y: 0.3, z: 0.68 },
  fov: 55,
};

/**
 * The same move on a portrait phone (`Framing.portrait`, D-246).
 *
 * At 390 × 844 the landscape keyframes keep their height and lose three
 * quarters of their width: the face frame became a wall of her flank with the
 * snout at the edge, and the end frame kept the rails and her mother and put
 * her out of it (her head at NDC x −0.94). Pulling the lens back does not
 * help, because the pen does not have the room: the first two candidates
 * stood in a littermate and in the kerb.
 *
 * So the lenses stay where the landscape ones stand and are re-aimed. The
 * face frame turns a few centimetres toward her snout and opens to 50°, which
 * puts her whole face beside the first rail. The end frame turns off the
 * lane's far side toward her and opens to 70°: her, small, under the rails,
 * with her mother's bulk filling the right, the same fact the landscape shot
 * states, stacked instead of spread. `portrait-openings.spec.ts` holds her
 * head in frame through the whole move.
 */
const REVEAL_FROM_PORTRAIT: ShotFrame = {
  position: { x: -0.55, y: 0.22, z: 0.16 },
  lookAt: { x: -0.09, y: 0.31, z: 0.21 },
  fov: 50,
};
const REVEAL_TO_PORTRAIT: ShotFrame = {
  position: { x: -0.72, y: 0.30, z: -0.16 },
  lookAt: { x: 0.27, y: 0.30, z: 0.32 },
  fov: 70,
};

/** One pair of reveal keyframes sampled at `k`, placed relative to her. */
function revealLens(from: ShotFrame, to: ShotFrame, self: { x: number; y: number; z: number }, k: number): ShotFrame {
  const lerp = (a: number, b: number): number => a + (b - a) * k;
  return {
    position: {
      x: self.x + lerp(from.position.x, to.position.x),
      y: lerp(from.position.y, to.position.y),
      z: self.z + lerp(from.position.z, to.position.z),
    },
    lookAt: {
      x: self.x + lerp(from.lookAt.x, to.lookAt.x),
      y: lerp(from.lookAt.y, to.lookAt.y),
      z: self.z + lerp(from.lookAt.z, to.lookAt.z),
    },
    fov: lerp(from.fov, to.fov),
  };
}

function easeInOut(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c < 0.5 ? 2 * c * c : 1 - ((-2 * c + 2) ** 2) / 2;
}

export interface BornDirectorOptions {
  /** Called when a boot lands, with the world position it landed at. */
  onBootfall(at: { x: number; y: number; z: number }): void;
  /** Called when her mother answers a nudge. */
  onSowAnswer(): void;
  /**
   * D-250's voices, each with the world point it comes from. `onSowAlarm`
   * is her mother answering the squeal she heard and could not see.
   */
  onSiblingSqueak?(at: { x: number; y: number; z: number }): void;
  onSiblingCry?(at: { x: number; y: number; z: number }, gain: number): void;
  onSowAlarm?(): void;
  /**
   * Called exactly once, at the moment the littermate leaves the scene.
   *
   * The mesh is this file's business; its COLLIDER is not, and a collider left
   * behind an object that is gone is an invisible wall in the one spot the last
   * stage of the beat asks the player to put her snout.
   */
  onSiblingTaken?(): void;
  /** The latch, lifted and dropped, with where it is. */
  onLatch?(at: { x: number; y: number; z: number }): void;
}

/**
 * How dark the lamp must actually BE before the littermate may be removed.
 *
 * Measured against `#lampLevel`, not against the stage clock. The first version
 * removed the sibling on the `taken` cue, which the beat fires at the same
 * instant it asks for the light back — so the removal and the light's return
 * were the same frame, and the human review of beat 1 reported seeing the piglet
 * go while the pen was still lit.
 *
 * Writing it as a threshold on the measured level rather than as a delay means
 * the guarantee survives somebody re-timing the stages in `born.ts`, which is
 * authored content and *will* be re-timed. The whole beat is that the player
 * does not see it happen; that is not a thing to leave to two numbers in
 * different files agreeing with each other.
 */
const TAKE_BELOW = 0.3;

export class BornDirector {
  readonly #bay: BornBay;
  readonly #options: BornDirectorOptions;
  readonly #fadeOverlay: FadeOverlay;

  /** 0 while the beat holds the camera, 1 once the player has it. */
  #cameraHeld = true;
  /** Seconds into the reveal move, or null when it is not running. */
  #revealT: number | null = null;
  /** Where he is in the taking, and for how long. See `TakingPhase`. */
  #phase: TakingPhase = 'parked';
  #phaseT = 0;
  /** The path he is walking and how far along it, metres. */
  #path: readonly Point[] = BORN_WALK_IN;
  #pathS = 0;
  /** His drawn heading, eased toward where he is going so a corner is a turn. */
  #heading = 0;
  /** The gate, as `penGateLeaf`'s `open`. */
  #gateOpen = 0;
  /** Seconds he has waited on her at the mouth this time (`MOUTH_GUARD_SECONDS`). */
  #guardT = 0;
  /** The cues he acts on once he is in place for them. */
  #bendWanted = false;
  #leaveWanted = false;
  /** Where she is, handed over each frame; null until the first. */
  #her: Point | null = null;
  #bootDistanceWalked = 0;
  /**
   * Total metres he has walked, never reset, driving the gait.
   *
   * Separate from `#bootDistanceWalked`, which is a stride countdown and is
   * subtracted from every footfall — swinging a leg off a sawtooth would snap it
   * back to the start of the cycle on every step.
   */
  #gaitDistance = 0;
  /** Target and current lamp intensity, so the shadow is a fall rather than a cut. */
  #lampTarget = 1;
  #lampLevel = 1;
  /**
   * Seconds the lamp must still stay down before it is allowed to come back.
   *
   * **Gentle Mode's "lengthens cutaways", and this is the only cutaway Act 1
   * has** (`src/shell/gentle.ts`). Beat 1's worst moment is authored as an
   * absence — the pen goes dark, a man leans in, and when the light returns the
   * littermate is not there. There is no shot to cut away from, so the cutaway
   * *is* the dark, and lengthening it means the light comes back later.
   *
   * Zero with the mode off, which is what keeps this from being a second timing
   * path: the countdown runs at 0 s and the cue behaves exactly as it always
   * did.
   */
  #lampHoldRemaining = 0;
  readonly #lampFull: number;
  /** How far he has bent into the pen, 0..1, and where it is going. */
  #lean = 0;
  #leanTarget = 0;
  /** Full intensity of each near batten, captured once so the dim can be undone. */
  readonly #nearFull: number[];
  /** True once the littermate has actually left the scene. Fires once. */
  #siblingTaken = false;
  /** How hard the mother is breathing. Drops when she settles. */
  #breathDepth = 1;
  #breathPhase = 0;
  #ended = false;
  /** Set when her mother has just been nudged; decays. Lifts her head. */
  #answer = 0;
  /** Shared presentation clock; no random timers means every replay matches. */
  #litterTime = 0;

  // ── D-250: the sibling awake, the cry, the carry ──────────────────────────
  /** The sim's last word on the sibling; the part hands it over every frame. */
  #sibling: SiblingSnapshot | null = null;
  #siblingShownAwake = false;
  #answersHeard = 0;
  readonly #siblingRig: RosalieRig;
  readonly #siblingAnim = new AnimationPlayer(ROSALIE_CLIPS, ROSALIE_ANIM);
  readonly #siblingPose: Pose = neutralPose();
  readonly #siblingState: CharacterState = {
    x: 0, y: 0, z: 0, facing: 0, speed: 0, velocityY: 0, gait: 'still', grounded: true,
    airborneTime: 0, landingImpact: 0, turnRate: 0, slopeGrade: 0, scramble: 0,
  };
  /** The drawn position, eased toward the sim's so a 60 Hz step does not judder. */
  #siblingDrawn: { x: number; z: number } | null = null;
  #carried = false;
  /** Seconds since the boots turned to leave, or null while they have not. */
  #leaveT: number | null = null;
  #criesPlayed = 0;
  /** Seconds since the squeal in the dark, for her mother's answer to it. */
  #alarmT: number | null = null;
  #alarmGrunts = 0;
  /** How far her mother's head is up for the squeal; slower than `#answer`. */
  #alarm = 0;

  /**
   * His hips and shoulders. Resolved once, by name, and it throws if they moved.
   *
   * Beat 1 walked a cropped pair of legs for twenty sessions, and a crop can get
   * away with sliding: the mid-stride pose it was authored in is the only pose
   * anybody ever sees. A whole man cannot (D-088, D-092), so the figure this
   * director walks is now rigged and this is what it holds it by.
   */
  readonly #workerParts: WorkerParts;

  /**
   * `doorHinge` is the walkway door's `door-hinge` node, which the hall owns:
   * he opens it to come in and to leave. `gate` is her pen's gate, taken out
   * of the dressing so it can swing. Only the pictures move; both colliders
   * stay shut through beat 1, since she cannot leave the pen
   * (`born-regions.spec.ts`), and nothing she can see says she could
   * (`born-gate.spec.ts`).
   */
  constructor(bay: BornBay, options: BornDirectorOptions, doorHinge: THREE.Object3D, gate: LivePenGate) {
    this.#bay = bay;
    this.#options = options;
    this.#doorHinge = doorHinge;
    this.#gate = gate;
    this.#workerParts = findWorkerParts(bay.boots);
    this.#siblingRig = new RosalieRig(bay.awakeSibling.plush);
    this.#lampFull = bay.lamp?.intensity ?? 0;
    this.#nearFull = bay.nearLights.map((light) => light.intensity);
    this.#fadeOverlay = new FadeOverlay();
  }

  /** True while the beat owns the camera. The game asks; it is not told. */
  get cameraHeld(): boolean {
    return this.#cameraHeld;
  }

  /** True once the closing cue has fired. */
  get ended(): boolean {
    return this.#ended;
  }

  /** How black the screen is, 0..1. Exposed for the harness. */
  get fade(): number {
    return this.#fadeOverlay.value;
  }

  /** Her mother answers. Called by the game when a nudge lands on her. */
  answer(): void {
    this.#answer = 1;
    this.#options.onSowAnswer();
  }

  /** The sim's sibling this frame (D-250). Presentation reads; it never writes back. */
  presentSibling(snapshot: SiblingSnapshot): void {
    this.#sibling = snapshot;
  }

  handle(cue: Cue): void {
    switch (cue.id) {
      case BORN_CUES.wake:
        // Start black and come up out of it, rather than starting on a picture.
        this.#fadeOverlay.snapToBlackAndFadeIn();
        this.#cameraHeld = true;
        break;
      case BORN_CUES.reveal:
        this.#revealT = 0;
        break;
      case BORN_CUES.handOver:
        this.#cameraHeld = false;
        this.#revealT = null;
        break;
      case BORN_CUES.bootsEnter:
        if (this.#phase === 'parked') this.#enter('walkIn', BORN_WALK_IN);
        break;
      case BORN_CUES.bootsArrive:
        // Nothing: he paces himself from the door to the mouth, and is there
        // before this cue's 1.6 s are up (`beat-part-module.spec.ts`).
        break;
      case BORN_CUES.lampBlocked:
        // He bends into the pen, and his body over the lamp is the dark. Not
        // here but once he is in the mouth (`#updateBoots`), so the dark is
        // never anything but him.
        this.#bendWanted = true;
        // Still on his walk in means the story was started at a later stage
        // (`skipToStage`, which enters every stage on the way in one frame, so
        // `bootsEnter` came with this cue). The dark must still be him, so he
        // is already in the mouth with the gate open. In play the walk in takes
        // 3.3 s of the 6.8 s before this cue: he is in the mouth, or stepping
        // into it after waiting on her, and is never moved.
        if (this.#phase === 'parked' || this.#phase === 'walkIn') {
          this.#enter('atMouth');
          this.#bay.boots.position.set(BORN_MOUTH_STANCE.x, 0, BORN_MOUTH_STANCE.z);
          this.#heading = BORN_MOUTH_STANCE.facing;
          this.#gateOpen = BORN_GATE_TAKE_OPEN;
        }
        break;
      case BORN_CUES.taken:
        // Too late to bend now: `skipToStage` past the shadow delivers
        // `lampBlocked` and this cue together, and a bend requested and never
        // done would happen after the take, hold the pen dark and keep him in
        // the mouth for good (Codex review, step 4).
        this.#bent = true;
        // He straightens and the light comes back. The removal is NOT here:
        // it happens in `#updateTaking`, the moment the pen is actually dark,
        // which by this cue has already been true for around two seconds.
        this.#leanTarget = 0;
        // He straightens immediately and the LIGHT waits. Gentle Mode holds
        // the dark for longer here and nowhere else; with the mode off this is
        // zero and the lamp target is set on this frame, as before.
        this.#lampHoldRemaining = gentleCutawayExtraSeconds();
        if (this.#lampHoldRemaining <= 0) this.#lampTarget = 1;
        // Belt and braces, and the braces are the point. If some future re-timing
        // ever runs the shadow so short that the lamp never crossed the
        // threshold, the beat must still lose a littermate — a beat 1 that ends
        // with both siblings present is not a shorter beat, it is a different
        // story. `#take()` is idempotent.
        this.#take();
        break;
      case BORN_CUES.bootsLeave:
        this.#leaveWanted = true;
        this.#leaveT = 0;
        break;
      case BORN_CUES.sowSettle:
        this.#breathDepth = 0.45;
        break;
      case BORN_CUES.fadeOut:
        this.#fadeOverlay.setTarget(1);
        break;
      case BORN_CUES.end:
        this.#ended = true;
        break;
      default:
        // Unknown cues are ignored rather than thrown on. A beat that emits
        // something this file has not learned yet should not take the render
        // step down; it should simply not be staged.
        break;
    }
  }

  /**
   * The framing the beat wants right now, or null to leave the camera alone.
   *
   * Positions are relative to Rosalie for the opening — she is the only thing in
   * the frame at that point and the shot has to work wherever she was spawned.
   */
  framing(self: { x: number; y: number; z: number }): Framing | null {
    const t = this.#revealT;
    // ── The other half of R-023's camera note ────────────────────────────────
    //
    // While the beat holds the camera but the reveal has not started — the whole
    // 2.2 s `dark` stage — this used to return null, which means "leave the
    // camera alone", which means **the gameplay follow camera**. The fade comes
    // up exponentially and is only 20 % black after one second, so the opening
    // read as: black, up onto the ordinary third-person shot of her back, then a
    // hard CUT to a close-up 0.46 m in front of her face, then the 6.4 s move.
    // That is the "another spot" in the report, and it is a cut in the first ten
    // seconds of the game.
    //
    // Holding the reveal's own first frame instead costs nothing and removes the
    // cut entirely: the picture fades up on the composition the move begins
    // from, and then the move begins. Same shot, no seam.
    if (t === null) return this.#cameraHeld ? this.#revealFraming(self, 0) : null;
    return this.#revealFraming(self, easeInOut(t / 6.4));
  }

  /**
   * The reveal move sampled at `k`, 0 at `REVEAL_FROM` and 1 at `REVEAL_TO`.
   *
   * Positions are relative to Rosalie: she is the only thing in frame at that
   * point and the shot has to work wherever she was spawned.
   */
  #revealFraming(self: { x: number; y: number; z: number }, k: number): Framing {
    return {
      ...revealLens(REVEAL_FROM, REVEAL_TO, self, k),
      portrait: revealLens(REVEAL_FROM_PORTRAIT, REVEAL_TO_PORTRAIT, self, k),
    };
  }

  /**
   * Runs on the render step. Presentation only. `her` is where she is, which
   * he waits on at the mouth (`MOUTH_GUARD_SECONDS`).
   */
  update(dt: number, her?: Point): void {
    if (her !== undefined) this.#her = { x: her.x, z: her.z };
    const frameDt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;

    if (this.#revealT !== null) this.#revealT += frameDt;

    this.#updateBoots(frameDt);
    this.#updateLean(frameDt);
    this.#updateLamp(frameDt);
    this.#updateTaking();
    this.#updateSow(frameDt);
    this.#updateLittermates(frameDt);
    this.#updateAwakeSibling(frameDt);
    this.#updateCarry(frameDt);

    this.#fadeOverlay.update(frameDt);
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
  }

  /** Into a phase, with the path it walks if it walks one. */
  #enter(phase: TakingPhase, path?: readonly Point[]): void {
    this.#phase = phase;
    this.#phaseT = 0;
    this.#guardT = 0;
    if (path !== undefined) {
      this.#path = path;
      this.#pathS = 0;
    }
  }

  /**
   * True while she is at the mouth and he has not yet waited his longest for
   * her to come away from it. Counts the wait.
   */
  #heldByHer(dt: number): boolean {
    const her = this.#her;
    if (her === null || this.#guardT >= MOUTH_GUARD_SECONDS) return false;
    if (distanceToMouth(her, this.#gate) >= MOUTH_GUARD_RADIUS) return false;
    this.#guardT += dt;
    return true;
  }

  /**
   * His whole part in the taking, one phase at a time (`TakingPhase`).
   *
   * Self-paced from `bootsEnter`: the story says when he comes, when he
   * bends, when he straightens and when he goes, and he does each once he is
   * where it can be done. From the door to the mouth is 2.8 s of walking and
   * 1.6 s at the gate, inside the 6.8 s the story gives him before the shadow.
   */
  #updateBoots(dt: number): void {
    const worker = this.#bay.boots;
    this.#phaseT += dt;
    let walking = false;
    let facing = this.#heading;
    let latchReach = 0;
    const walk = (): boolean => {
      const length = pathLength(this.#path);
      this.#pathS = Math.min(length, this.#pathS + BOOT_SPEED * dt);
      const at = pointOnPath(this.#path, this.#pathS);
      worker.position.set(at.x, 0, at.z);
      facing = at.heading;
      walking = true;
      return this.#pathS >= length - 1e-9;
    };
    const swung = (): number => easeInOut(this.#phaseT / GATE_SWING_SECONDS);

    switch (this.#phase) {
      case 'parked': {
        const park = BORN_WALK_IN[0];
        if (park !== undefined) worker.position.set(park.x, 0, park.z);
        facing = 0;
        break;
      }
      case 'walkIn':
        if (walk()) this.#enter('unlatch');
        break;
      case 'unlatch':
        facing = BORN_LATCH_STANCE.facing;
        latchReach = Math.min(1, this.#phaseT / LATCH_LIFT_AT);
        if (this.#phaseT >= LATCH_LIFT_AT && this.#phaseT - dt < LATCH_LIFT_AT) this.#latchSound();
        if (this.#phaseT >= UNLATCH_SECONDS) this.#enter('swingOpen');
        break;
      case 'swingOpen':
        facing = BORN_LATCH_STANCE.facing;
        latchReach = 1;
        if (this.#phaseT - dt <= 0 && this.#heldByHer(dt)) {
          this.#phaseT = 0;
          break;
        }
        this.#gateOpen = BORN_GATE_TAKE_OPEN * swung();
        if (this.#phaseT >= GATE_SWING_SECONDS) this.#enter('stepIn', BORN_STEP_IN);
        break;
      case 'stepIn':
        if (walk()) this.#enter('atMouth');
        break;
      case 'atMouth':
        facing = BORN_MOUTH_STANCE.facing;
        if (this.#bendWanted && !this.#bent) {
          // The body over the lamp, and the dark with it.
          this.#bent = true;
          this.#leanTarget = 1;
          // 0.08, not 0.22. A man bent over a pen blocks a lamp rather than
          // switching it off, and the residue is what stops this reading as a
          // power cut — which would be an event, and this must not be an event.
          // But 0.22 of a lamp under five lit ceiling battens is not a shadow at
          // all: the review of beat 1 could not see that the pen had gone dark,
          // and therefore could not see that the taking happened inside the dark.
          this.#lampTarget = 0.08;
        }
        if (this.#leaveWanted && this.#leanTarget === 0 && this.#lean < 0.15 && !this.#heldByHer(dt)) {
          this.#enter('stepOut', [...BORN_STEP_IN].reverse());
        }
        break;
      case 'stepOut':
        if (walk()) this.#enter('swingShut');
        break;
      case 'swingShut':
        facing = BORN_LATCH_STANCE.facing;
        latchReach = 1;
        this.#gateOpen = BORN_GATE_TAKE_OPEN * (1 - swung());
        if (this.#phaseT >= GATE_SWING_SECONDS) {
          this.#gateOpen = 0;
          this.#latchSound();
          this.#enter('latchDrop');
        }
        break;
      case 'latchDrop':
        facing = BORN_LATCH_STANCE.facing;
        latchReach = 1 - Math.min(1, this.#phaseT / LATCH_DROP_SECONDS);
        if (this.#phaseT >= LATCH_DROP_SECONDS) this.#enter('walkOut', [...BORN_WALK_IN].reverse());
        break;
      case 'walkOut':
        if (walk()) this.#enter('gone');
        break;
      case 'gone':
        // In the vestibule with his back to the hall, as he arrived there.
        facing = Math.PI;
        break;
    }

    this.#gate.set(this.#gateOpen);
    // Turning, not snapping: a corner of his path is a turn of the body.
    this.#heading += wrapAngle(facing - this.#heading) * (1 - Math.exp(-10 * dt));
    worker.rotation.y = this.#heading;

    // AFTER the position has been applied, not before.
    //
    // `onBootfall` is documented as receiving the world position the boot landed
    // at, and it was being handed the position from the PREVIOUS frame — so every
    // spatialised footfall played from a step behind the boots you can see. A
    // small error, and precisely the kind that makes audio feel detached without
    // anyone being able to say why.
    if (walking) {
      const step = BOOT_SPEED * dt;
      this.#bootDistanceWalked += step;
      this.#gaitDistance += step;
      if (this.#bootDistanceWalked >= BOOT_STRIDE) {
        this.#bootDistanceWalked -= BOOT_STRIDE;
        this.#options.onBootfall(worker.position);
      }
    }

    // Off distance travelled, not off a clock, so the leg that swings forward is
    // the one the footfall above just played. `LatchDirector` swings beat 2's man
    // by the same rule and from the same constant; the two beats share a walk
    // because they share a hall and, as far as the player knows, a man.
    this.#pose(walking ? 1 : 0, latchReach, dt);

    // The door opens for him while he walks near it and shuts behind him
    // once he stops: in the vestibule, on the way out, with her littermate.
    const moving = this.#phase === 'walkIn' || this.#phase === 'walkOut';
    this.#doorHinge.rotation.y = this.#door.step(dt, { x: worker.position.x, z: worker.position.z, moving });
  }

  /** The latch, lifted or dropped, from the gate's free end. */
  #latchSound(): void {
    const g = this.#gate;
    this.#options.onLatch?.({ x: g.hinge.x, y: 0.55, z: g.hinge.z - g.swing * PEN_GATE.length });
  }

  /** Set once he has bent in for the take, so the cue is acted on once. */
  #bent = false;

  /**
   * Swing his hips and shoulders, bend him, or ease them all back to standing.
   *
   * `walking` is 0 or 1 rather than a boolean because it is a multiplier on the
   * amplitude: at rest the target is a straight leg, and the same exponential
   * settle carries him into it. A stopped man whose legs snap upright between
   * two render frames is the one thing worse than a man who slides.
   *
   * `latchReach` (0..1) is his right hand out to the latch with a small bend.
   * Facing the pens, his right is toward the mouth. The bend into the pen is
   * `#lean`, both arms going forward with it; the arm with her littermate in
   * it settles to a held carry as he straightens.
   */
  #pose(walking: 0 | 1, latchReach: number, frameDt: number): void {
    const swing = walking * Math.sin((this.#gaitDistance / BOOT_STRIDE) * Math.PI);
    const settle = 1 - Math.exp(-8 * frameDt);
    const pose = (node: THREE.Object3D, target: number): void => {
      node.rotation.x += (target - node.rotation.x) * settle;
    };
    const lean = this.#lean;
    // The bend follows `#lean` exactly: it is already eased, and the dark is
    // timed off it (`TAKE_BELOW`), so it must not lag behind it.
    this.#workerParts.waist.rotation.x = BEND_TAKE * lean + BEND_LATCH * latchReach * (1 - lean);
    pose(this.#workerParts.hips[0], swing * 0.42);
    pose(this.#workerParts.hips[1], -swing * 0.42);
    const reachIn = -0.95 * lean;
    pose(this.#workerParts.shoulders[0], reachIn + (1 - lean) * -swing * 0.26);
    // The arm with her littermate in it does not swing. A little forward,
    // held: the way anybody carries something that is trying to get away.
    const right = this.#carried ? -0.22 : swing * 0.26 - 0.9 * latchReach;
    pose(this.#workerParts.shoulders[1], reachIn + (1 - lean) * right);
  }

  readonly #doorHinge: THREE.Object3D;
  /** Presentation only in this beat: see the constructor. */
  readonly #door = new DoorSwing(false);
  /** Her pen's gate. Presentation only in this beat: see the constructor. */
  readonly #gate: LivePenGate;

  /**
   * His bend into the pen, and the room light it takes with it.
   *
   * Asymmetric for the same reason the lamp is: a body arrives over a pen slowly
   * — somebody stoops, they do not fall — and leaves the instant they straighten
   * up. The two rates are deliberately close to the lamp's so the body and the
   * shadow it casts are read as one movement rather than two.
   */
  #updateLean(dt: number): void {
    const rate = this.#leanTarget > this.#lean ? 2.6 : 6;
    this.#lean += (this.#leanTarget - this.#lean) * (1 - Math.exp(-rate * dt));
    if (this.#lean < 0.0005) this.#lean = 0;

    // The battens over this stretch of walkway are why the creep was never dark.
    //
    // Down to 30 %, not to nothing: the hall beyond the bay must stay lit, or
    // this stops being a shadow over one pen and becomes the power cut the beat
    // is not about.
    //
    // **0.88 was tried, measured, and reverted.** Taking these to 12 % instead
    // of 30 % moved the batten level by more than half and moved the actual
    // picture almost not at all: mean luminance in the shadow went 0.282 to
    // 0.255, against 0.550 once the light is back. So whatever is still lighting
    // the creep during the shadow, it is NOT these five battens, and dimming
    // them harder only buys a bigger deviation from staging a human has already
    // reviewed. The residual source has not been identified; do not tune this
    // number again until it has.
    for (const [i, light] of this.#bay.nearLights.entries()) {
      const full = this.#nearFull[i];
      if (full === undefined) continue;
      light.intensity = full * (1 - 0.7 * this.#lean);
    }
  }

  /**
   * Remove the littermate, but only once the pen is genuinely dark.
   *
   * This is the single invariant the beat cannot be allowed to break, so it is
   * expressed as a condition on the measured state of the room rather than as a
   * moment on a clock.
   */
  #updateTaking(): void {
    if (this.#siblingTaken) return;
    if (this.#leanTarget !== 1) return;
    if (this.#lampLevel > TAKE_BELOW) return;
    if (this.#lean < 0.9) return;
    this.#take();
  }

  #take(): void {
    if (this.#siblingTaken) return;
    this.#siblingTaken = true;
    // It happens in the dark, and until D-250 that was the whole of it: no
    // hand and no sound, and the light came back on a gap. Alex played it as
    // a piglet despawning. So the dark stays — the player still does not SEE
    // the lift — but they hear it, and when the light comes back the man is
    // straightening up with her littermate in his hand.
    this.#bay.takenSibling.removeFromParent();
    const cryAt = this.#sibling === null || this.#sibling.phase === 'asleep'
      ? { x: SIBLING_TAKEN_AT.x, y: DECK_Y + 0.2, z: SIBLING_TAKEN_AT.z }
      : { x: this.#sibling.x, y: DECK_Y + 0.2, z: this.#sibling.z };
    this.#pickUp();
    this.#options.onSiblingCry?.(cryAt, 1);
    this.#criesPlayed += 1;
    this.#alarmT = 0;
    this.#options.onSiblingTaken?.();
  }

  /** Into his hand: the awake plush, woken or not, re-parented to his right arm. */
  #pickUp(): void {
    const awake = this.#bay.awakeSibling;
    const hand = this.#workerParts.shoulders[1];
    awake.root.removeFromParent();
    hand.add(awake.root);
    awake.root.position.set(CARRY_IN_HAND.x, CARRY_IN_HAND.y, CARRY_IN_HAND.z);
    awake.root.rotation.set(0, 0, 0);
    awake.roll.rotation.set(0, 0, 0);
    awake.roll.position.set(0, 0, 0);
    awake.root.visible = true;
    this.#carried = true;
  }

  #updateCarry(dt: number): void {
    if (this.#alarmT !== null) {
      this.#alarmT += dt;
      const next = SOW_ALARM_GRUNTS[this.#alarmGrunts];
      if (next !== undefined && this.#alarmT >= next) {
        this.#alarmGrunts += 1;
        this.#options.onSowAlarm?.();
      }
      // Her head comes up and stays up while she is calling.
      if (this.#alarmGrunts < SOW_ALARM_GRUNTS.length) this.#alarm = 1;
    }
    this.#alarm *= Math.exp(-0.35 * dt);

    if (!this.#carried) return;
    const awake = this.#bay.awakeSibling;
    // Held, and not still: a squirm in bursts, the body twisting in his grip
    // and the legs going. Driven off the cry schedule so it fights while it
    // screams and hangs between.
    const t = this.#leaveT ?? 0;
    const lastCry = CARRIED_CRY_AT.reduce((at, [cryT]) => (cryT <= t ? cryT : at), -10);
    const struggling = t - lastCry < 1.0 ? 1 : 0.25;
    const wiggle = Math.sin(this.#litterTime * 17) * struggling;
    awake.root.rotation.z = 0.28 * wiggle;
    const pose = this.#siblingPose;
    Object.assign(pose, neutralPose());
    pose.legFL.swing = 0.5 * wiggle - 0.2;
    pose.legFR.swing = -0.5 * wiggle - 0.2;
    pose.legBL.swing = -0.45 * wiggle + 0.3;
    pose.legBR.swing = 0.45 * wiggle + 0.3;
    pose.headPitch = -0.25 + 0.2 * wiggle;
    pose.earLFlick = 0.4 * struggling;
    pose.earRFlick = 0.4 * struggling;
    this.#siblingRig.apply(pose);

    if (this.#leaveT === null) return;
    this.#leaveT += dt;
    const cry = CARRIED_CRY_AT[this.#criesPlayed - 1];
    if (cry !== undefined && this.#leaveT >= cry[0]) {
      const at = awake.root.getWorldPosition(this.#cryAt);
      const beyond = at.z < WALKWAY_DOOR_AT.z;
      this.#options.onSiblingCry?.({ x: at.x, y: at.y, z: at.z }, cry[1] * (beyond ? DOOR_MUFFLE : 1));
      this.#criesPlayed += 1;
    }
  }

  /** Scratch for the hand's world position. */
  readonly #cryAt = new THREE.Vector3();

  /**
   * The sibling awake: swapped in for the merged sleeper the frame it wakes,
   * then posed from the sim's snapshot through its own `RosalieRig` (D-250).
   */
  #updateAwakeSibling(dt: number): void {
    const s = this.#sibling;
    if (s === null || this.#siblingTaken || s.phase === 'asleep') return;
    const awake = this.#bay.awakeSibling;
    if (!this.#siblingShownAwake) {
      this.#siblingShownAwake = true;
      // Hidden, not removed: `siblingPresent` reads the sleeper's parent and
      // must stay true until the taking. The awake plush starts on its side,
      // exactly where and how the sleeper lay, so the swap is not a pop.
      this.#bay.takenSibling.visible = false;
      awake.root.visible = true;
    }
    if (s.answers > this.#answersHeard) {
      this.#answersHeard = s.answers;
      this.#options.onSiblingSqueak?.({ x: s.x, y: DECK_Y + 0.22, z: s.z });
      this.#squeakT = 0;
    }
    this.#squeakT += dt;

    const drawn = this.#siblingDrawn ?? { x: s.x, z: s.z };
    this.#siblingDrawn = drawn;
    const follow = 1 - Math.exp(-30 * dt);
    drawn.x += (s.x - drawn.x) * follow;
    drawn.z += (s.z - drawn.z) * follow;
    awake.root.position.set(drawn.x, DECK_Y, drawn.z);
    awake.root.rotation.y = s.yaw;

    // Onto its side and up again about its own middle, the way a box rolls:
    // at 0 the pivot is its centre height, at π/2 its half-width, so a flank
    // and not a centre ends up on the straw.
    const lie = 1 - s.stand;
    const r = lie * (Math.PI / 2);
    awake.roll.rotation.z = r;
    awake.roll.position.y = AWAKE_SIBLING_CENTRE_Y * Math.cos(r) + AWAKE_SIBLING_HALF_WIDTH * Math.sin(r);

    const state = this.#siblingState;
    state.speed = s.speed;
    state.facing = s.yaw;
    state.gait = s.speed > 0.05 ? 'trot' : 'still';
    const pose = this.#siblingPose;
    this.#siblingAnim.update(dt, state, pose);
    // Curled when down, as the sleeper is: front pair forward, back pair under.
    pose.legFL.swing -= 0.6 * lie;
    pose.legFR.swing -= 0.5 * lie;
    pose.legBL.swing += 0.6 * lie;
    pose.legBR.swing += 0.5 * lie;
    // Nosing the straw when it has stood a while: what she has just been
    // taught, done beside her by somebody who is not being taught anything.
    if (s.phase === 'following' && s.stillFor > 1.2) {
      const k = Math.min(1, (s.stillFor - 1.2) / 0.5);
      pose.headPitch += k * (0.32 + 0.1 * Math.sin(this.#litterTime * 6.5));
      pose.snoutPitch += k * 0.12;
    }
    // Ears up for half a second after it squeaks: attention, toward her.
    const perk = Math.max(0, 1 - this.#squeakT / 0.5);
    pose.earLFlick += 0.35 * perk;
    pose.earRFlick += 0.35 * perk;
    this.#siblingRig.apply(pose);
  }

  #squeakT = 10;

  /** D-250's diagnostics: what the director last presented of the sibling. */
  get siblingPhase(): SiblingSnapshot['phase'] {
    return this.#sibling?.phase ?? 'asleep';
  }

  get carried(): boolean {
    return this.#carried;
  }

  get cries(): number {
    return this.#criesPlayed;
  }

  get carriedAt(): { x: number; y: number; z: number } | null {
    if (!this.#carried) return null;
    const at = this.#bay.awakeSibling.root.getWorldPosition(new THREE.Vector3());
    return { x: at.x, y: at.y, z: at.z };
  }

  /** True once the littermate has left the scene. Test seam — see `TAKE_BELOW`. */
  get siblingTaken(): boolean {
    return this.#siblingTaken;
  }

  /** The lamp's level, 0..1. Exposed so a test can assert the pen went dark. */
  get lampLevel(): number {
    return this.#lampLevel;
  }

  /** How far the lean has arrived, 0..1. Exposed so evidence can place the block. */
  get lean(): number {
    return this.#lean;
  }

  /**
   * How much of the near room light is still live, 0..1.
   *
   * This is deliberately read from the lights rather than reconstructed from
   * `#lean`: the evidence needs to catch a disconnect between what the lean
   * asked for and the 0.7 batten dim it was meant to cause.
   */
  get nearLightLevel(): number {
    if (this.#bay.nearLights.length === 0) return 1;

    let current = 0;
    let full = 0;
    for (const [i, light] of this.#bay.nearLights.entries()) {
      const captured = this.#nearFull[i];
      if (captured === undefined) return 1;
      current += light.intensity;
      full += captured;
    }

    if (full <= 0) return 1;
    return Math.min(1, Math.max(0, current / full));
  }

  #updateLamp(dt: number): void {
    const lamp = this.#bay.lamp;
    if (!lamp) return;
    // Deliberately slow on the way down and quick on the way back, because that
    // is how a body moving over a lamp actually reads: the shadow arrives as
    // somebody leans in, and leaves the instant they straighten up.
    // Runs before the lamp moves, so the frame the hold expires is the frame the
    // light starts coming back rather than the one after it.
    if (this.#lampHoldRemaining > 0) {
      this.#lampHoldRemaining -= dt;
      if (this.#lampHoldRemaining <= 0) this.#lampTarget = 1;
    }

    const rate = this.#lampTarget < this.#lampLevel ? 3.2 : 7;
    this.#lampLevel += (this.#lampTarget - this.#lampLevel) * (1 - Math.exp(-rate * dt));
    lamp.intensity = this.#lampFull * this.#lampLevel;
  }

  #updateSow(dt: number): void {
    // 16 breaths a minute at rest. Slow enough that the pause at the top of each
    // one is visible, and that pause is the entire performance — she has no face
    // (D-002), so this is the only thing that separates her from a prop.
    // Faster while she is calling after the squeal (D-250): the one time in
    // the beat she is anything but resting.
    this.#breathPhase += dt * (Math.PI * 2 * 16 * (1 + 0.8 * this.#alarm)) / 60;
    if (this.#breathPhase > Math.PI * 2) this.#breathPhase -= Math.PI * 2;
    // Not a sine. A real breath is a quick fill and a long fall, and a symmetric
    // one reads as a machine — which, in a building full of machines, is the one
    // thing she must not read as.
    const s = Math.sin(this.#breathPhase);
    const swell = (s > 0 ? s ** 0.6 : -((-s) ** 1.5)) * 0.5 + 0.5;
    this.#bay.sow.breath.scale.y = 1 + 0.016 * swell * this.#breathDepth;

    this.#answer *= Math.exp(-1.1 * dt);
    if (this.#answer < 0.002) this.#answer = 0;
    // She lifts her head as far as the lane allows, which is not far, and puts it
    // down again. Attention, never emotion (D-002) — and here the attention is
    // precisely what the cage refuses.
    // For the squeal, as high as it goes and held while she calls: she heard
    // it and cannot turn round (D-250). Still attention — it is where her
    // head points, not a face.
    this.#bay.sow.head.rotation.x = SOW_HEAD_LIFT_MAX * Math.max(this.#answer / 1.4, this.#alarm);
  }

  #updateLittermates(dt: number): void {
    this.#litterTime += dt;
    const breathPhase = this.#litterTime * (Math.PI * 2 * LITTER_BREATHS_PER_MINUTE) / 60;

    // Their cycles deliberately share a ten-second clock but begin five seconds
    // apart. That guarantees the 1.1 s twitches never overlap, while keeping a
    // deterministic sleeping rhythm every ten seconds for each animal — inside
    // the authored 7–13 s window, with no timer or random source to desynchronise
    // a replay.
    this.#updateLittermate(this.#bay.keptSibling, breathPhase + 0.73, 1.15);
    this.#updateLittermate(this.#bay.takenSiblingParts, breathPhase + 3.91, 6.15);
  }

  #updateLittermate(parts: LittermateParts, breathPhase: number, twitchPhase: number): void {
    // Same quick-fill/long-fall curve as the sow. A sine makes a sleeping animal
    // read as a pump, especially beside industrial machinery; its asymmetric
    // exponents make the inhale arrive and the exhale settle.
    const s = Math.sin(breathPhase);
    const swell = (s > 0 ? s ** 0.6 : -((-s) ** 1.5)) * 0.5 + 0.5;
    const scale = 1 + LITTER_BREATH_AMPLITUDE * swell;
    parts.breath.scale.x = scale;
    // `piglet-lie` maps local X onto world Y. Scaling it otherwise lifts the
    // lowest vertex; this measured slope restores that vertex to its resting
    // floor point, so the breath swells the flank rather than making her hover.
    parts.breath.position.y = parts.breathRestY - (scale - 1) * parts.breathFloorSlope;

    const cycle = ((this.#litterTime - twitchPhase) % 10 + 10) % 10;
    const u = cycle / LITTER_TWITCH_DURATION;
    // One raised-and-returned pulse, not a gait: sin(pi*u) is zero at both ends
    // and has no discontinuity when the ten-second schedule wraps.
    const pulse = u < 1 ? Math.sin(Math.PI * u) : 0;
    parts.twitch.rotation.x = parts.twitchRestX + LITTER_TWITCH_AMPLITUDE * pulse;
  }

  /** Where the empty patch is, so the world can put an interactable on it. */
  static get emptyAt(): { x: number; y: number; z: number } {
    return { x: SIBLING_TAKEN_AT.x, y: SIBLING_TAKEN_AT.y + 0.05, z: SIBLING_TAKEN_AT.z };
  }

  /** Where the lamp is, for the record. */
  static get lampAt(): { x: number; y: number; z: number } {
    return { ...LAMP_AT };
  }

  /** The bay's centre, for framing. */
  static get bayZ(): number {
    return BAY_Z;
  }
}
